"use client";

/**
 * "필요해요" 수요 체크 블록 — 블로그 본문 마커 · 검색 0건 화면.
 *
 * 연회색 박스 하나: 제목 · 설명 한 문장 · [도시 선택][필요해요] 한 줄.
 * 누르면 로그인 사용자는 바로 "열리면 알려드릴게요"(알림 자동 신청), 비로그인은
 * "열리면 이메일로 알려드릴까요?" 링크 → 그 자리에서 이메일 한 줄 입력으로 펼친다.
 * 누적 수는 10명 이상일 때만 보인다.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { truncateKeyword } from "@/lib/search/empty-state";
import {
  demandCityLabel,
  demandItemName,
  getDemandCityGroups,
  normalizeDemandCityId,
  toDemandLocale,
} from "@/lib/demand/demand-items";
import {
  getDemandStateAction,
  setDemandNotifyAction,
  toggleDemandRequestAction,
  type DemandState,
} from "@/app/actions/demand-request";

const DEVICE_ID_KEY = "locarora:demand-device-id";
/** 이 수 이상일 때만 "N명이 필요하다고 했어요"를 보인다. */
const COUNT_VISIBLE_MIN = 10;

// 저장소가 막힌 환경(사파리 개인 모드·차단된 사이트 데이터)에서도 한 페이지 안에서는
// 같은 id 를 쓰도록 메모리에 들고 있는다.
let memoryDeviceId: string | null = null;

function createDeviceId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand()}-${rand()}`;
}

function getOrCreateDeviceId(): string {
  if (memoryDeviceId) return memoryDeviceId;
  let id: string | null = null;
  try {
    id = window.localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    id = null;
  }
  if (!id || !/^[A-Za-z0-9-]{16,64}$/.test(id)) {
    id = createDeviceId();
    try {
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    } catch {
      // 저장 실패 — 메모리 값으로만 쓴다.
    }
  }
  memoryDeviceId = id;
  return id;
}

/** 한국어 목적격 조사(을/를). 끝 글자가 한글 음절이 아니면 "을(를)". */
function objectParticle(word: string): string {
  const code = word.charCodeAt(word.length - 1);
  if (code >= 0xac00 && code <= 0xd7a3) {
    return (code - 0xac00) % 28 === 0 ? "를" : "을";
  }
  return "을(를)";
}

interface DemandRequestBlockProps {
  source: "blog" | "search";
  /** 블로그 블록 — 품목 키(demand-items 사전) */
  itemKey?: string | null;
  /** 검색 블록 — 0건 검색어 원문 */
  query?: string | null;
  /** 기본 선택 도시(CITIES id). 없으면 "지역 선택" 상태 */
  defaultCityId?: string | null;
  className?: string;
}

export function DemandRequestBlock({
  source,
  itemKey = null,
  query = null,
  defaultCityId = null,
  className,
}: DemandRequestBlockProps) {
  const t = useTranslations("demand");
  const locale = toDemandLocale(useLocale());
  const { status: authStatus } = useAuth();
  const isSignedIn = authStatus === "authenticated";
  const selectId = useId();

  const trimmedQuery = query?.trim() || null;
  const target = useMemo(
    () => (itemKey ? { itemKey, query: null } : { itemKey: null, query: trimmedQuery }),
    [itemKey, trimmedQuery],
  );

  const [cityId, setCityId] = useState<string>(
    () => normalizeDemandCityId(defaultCityId) ?? "",
  );
  const [state, setState] = useState<DemandState | null>(null);
  const [pending, setPending] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailPending, setEmailPending] = useState(false);
  const requestSeq = useRef(0);

  const cityGroups = useMemo(() => getDemandCityGroups(locale), [locale]);

  // 도시·로그인 상태가 정해지면 내 상태(눌렀는지·알림 신청했는지)와 누적 수를 읽는다.
  useEffect(() => {
    if (!cityId || authStatus === "loading") return;
    const seq = ++requestSeq.current;
    getDemandStateAction({
      ...target,
      cityId,
      deviceId: getOrCreateDeviceId(),
    })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setState(res.status === "ok" ? res.data : null);
      })
      .catch(() => {
        if (seq === requestSeq.current) setState(null);
      });
  }, [cityId, authStatus, target]);

  const active = !!cityId && state?.active === true;

  // 로그인 사용자는 알림을 자동으로 신청한다(로그인 전 이 기기로 눌러 둔 행 포함).
  const autoNotifyKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isSignedIn || !active || state?.notify) return;
    const key = `${cityId}|${target.itemKey ?? target.query}`;
    if (autoNotifyKey.current === key) return;
    autoNotifyKey.current = key;
    setDemandNotifyAction({
      ...target,
      cityId,
      deviceId: getOrCreateDeviceId(),
      email: null,
    })
      .then((res) => {
        if (res.status === "ok") {
          setState((prev) => (prev ? { ...prev, notify: true } : prev));
        }
      })
      .catch(() => {
        // 표시만 그대로 둔다 — 다음 방문 때 다시 시도된다.
      });
  }, [isSignedIn, active, state?.notify, cityId, target]);

  const handleCityChange = (next: string) => {
    requestSeq.current += 1;
    setCityId(next);
    setState(null);
    setEmailOpen(false);
  };

  const handleToggle = useCallback(async () => {
    if (!cityId || pending) return;
    setPending(true);
    const seq = ++requestSeq.current;
    try {
      const res = await toggleDemandRequestAction({
        ...target,
        cityId,
        cityLabel: demandCityLabel(cityId, locale),
        locale,
        source,
        sourcePath:
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}`.slice(0, 500)
            : null,
        deviceId: getOrCreateDeviceId(),
      });
      if (res.status !== "ok") {
        toast.error(t("failed"));
        return;
      }
      if (seq !== requestSeq.current) return;
      setState({ ...res.data, hasEmail: false });
      if (!res.data.active) setEmailOpen(false);
    } catch {
      toast.error(t("failed"));
    } finally {
      setPending(false);
    }
  }, [cityId, pending, target, locale, source, t]);

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = email.trim();
    if (!value || !cityId || emailPending) return;
    setEmailPending(true);
    try {
      const res = await setDemandNotifyAction({
        ...target,
        cityId,
        deviceId: getOrCreateDeviceId(),
        email: value,
      });
      if (res.status !== "ok") {
        toast.error(t("failed"));
        return;
      }
      setState((prev) => (prev ? { ...prev, notify: true, hasEmail: true } : prev));
      setEmailOpen(false);
    } catch {
      toast.error(t("failed"));
    } finally {
      setEmailPending(false);
    }
  };

  let title: string;
  let description: string;
  if (itemKey) {
    const item = demandItemName(itemKey, locale);
    title = t("blogTitle");
    description = t("blogDescription", { item, itemObject: item + objectParticle(item) });
  } else {
    const shown = truncateKeyword(trimmedQuery ?? "");
    title = t("searchTitle");
    description = t("searchDescription", {
      query: shown,
      queryObject: `'${shown}'${objectParticle(shown)}`,
    });
  }

  if (!itemKey && !trimmedQuery) return null;

  const count = cityId ? (state?.count ?? 0) : 0;
  const notifyOn = state?.notify === true || isSignedIn;

  return (
    <div className={cn("not-prose rounded-xl bg-gray-50 p-4", className)}>
      <p className="text-[15px] font-semibold text-gray-900">{title}</p>
      <p className="mt-1 text-sm leading-6 text-gray-600">{description}</p>

      <div className="mt-3 flex items-center gap-2">
        <label htmlFor={selectId} className="sr-only">
          {t("cityLabel")}
        </label>
        <select
          id={selectId}
          value={cityId}
          onChange={(e) => handleCityChange(e.target.value)}
          className={cn(
            "h-9 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 text-sm sm:w-44 sm:flex-none",
            cityId ? "text-gray-900" : "text-gray-500",
          )}
        >
          <option value="">{t("cityPlaceholder")}</option>
          {cityGroups.map((group) => (
            <optgroup key={group.countryCode} label={group.countryLabel}>
              {group.cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <button
          type="button"
          onClick={handleToggle}
          disabled={!cityId || pending}
          aria-pressed={active}
          className={cn(
            "h-9 shrink-0 rounded-lg px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed",
            active
              ? "border border-gray-900 bg-white text-gray-900"
              : "bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300",
          )}
        >
          {active ? t("needed") : t("need")}
        </button>
      </div>

      {count >= COUNT_VISIBLE_MIN && (
        <p className="mt-2 text-xs text-gray-500">{t("count", { count })}</p>
      )}

      {active &&
        (notifyOn ? (
          <p className="mt-2 text-sm text-gray-700">{t("willNotify")}</p>
        ) : emailOpen ? (
          <form onSubmit={handleEmailSubmit} className="mt-3">
            <div className="flex items-center gap-2">
              <label htmlFor={`${selectId}-email`} className="sr-only">
                {t("emailLabel")}
              </label>
              <input
                id={`${selectId}-email`}
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("emailPlaceholder")}
                className="h-9 min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 text-base text-gray-900 placeholder:text-gray-400 sm:text-sm"
              />
              <button
                type="submit"
                disabled={emailPending}
                className="h-9 shrink-0 rounded-lg bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-300"
              >
                {t("emailSubmit")}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">{t("emailNote")}</p>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            className="mt-2 text-sm text-gray-600 underline underline-offset-4 hover:text-gray-900"
          >
            {t("askEmail")}
          </button>
        ))}
    </div>
  );
}
